/* ============================================================
   Common Questions — accordion behaviour
   - Smooth height animation (auto height after open so content
     reflows on resize / font load).
   - Single-open (default) or allow-multiple.
   - Full keyboard + screen-reader support (aria-expanded / hidden).
   - Respects prefers-reduced-motion.
   - Works inside the Shopify theme editor (re-inits on load,
     opens the block being edited).
   Multiple instances on one page are supported — everything is
   scoped to each .cq-section.
   ============================================================ */
(function () {
  "use strict";

  var REDUCED_MOTION = window.matchMedia("(prefers-reduced-motion: reduce)");

  function CQAccordion(root) {
    this.root = root;
    this.allowMultiple = root.hasAttribute("data-allow-multiple");
    this.items = Array.prototype.slice.call(root.querySelectorAll(".cq-item"));
    this.bind();
    this.initialState();
  }

  CQAccordion.prototype.bind = function () {
    var self = this;
    this.items.forEach(function (item) {
      var btn = item.querySelector(".cq-item__question");
      var panel = item.querySelector(".cq-item__answer");
      if (!btn || !panel) return;

      btn.addEventListener("click", function () {
        self.toggle(item);
      });

      // Clean up after a close animation so the panel is fully hidden
      // from assistive tech and can't be tabbed into.
      panel.addEventListener("transitionend", function (e) {
        if (e.propertyName !== "height") return;
        if (!item.classList.contains("is-open")) {
          panel.hidden = true;
        } else {
          // Let content grow/shrink naturally once open.
          panel.style.height = "auto";
        }
      });
    });
  };

  CQAccordion.prototype.initialState = function () {
    var self = this;
    this.items.forEach(function (item) {
      var isOpen = item.classList.contains("is-open");
      self.setState(item, isOpen, /* instant */ true);
    });
  };

  CQAccordion.prototype.toggle = function (item) {
    var willOpen = !item.classList.contains("is-open");

    if (willOpen && !this.allowMultiple) {
      var self = this;
      this.items.forEach(function (other) {
        if (other !== item && other.classList.contains("is-open")) {
          self.setState(other, false, false);
        }
      });
    }

    this.setState(item, willOpen, false);
  };

  CQAccordion.prototype.setState = function (item, open, instant) {
    var btn = item.querySelector(".cq-item__question");
    var panel = item.querySelector(".cq-item__answer");
    if (!btn || !panel) return;

    btn.setAttribute("aria-expanded", open ? "true" : "false");
    item.classList.toggle("is-open", open);

    var noMotion = instant || REDUCED_MOTION.matches;

    if (open) {
      panel.hidden = false;
      if (noMotion) {
        panel.style.height = "auto";
        return;
      }
      // From 0 (or current) → measured height, then JS releases to auto.
      var start = panel.offsetHeight; // force reflow, capture current
      panel.style.height = start + "px";
      var target = panel.scrollHeight;
      requestAnimationFrame(function () {
        panel.style.height = target + "px";
      });
    } else {
      if (noMotion) {
        panel.style.height = "0px";
        panel.hidden = true;
        return;
      }
      // From auto → fixed px → 0 so the transition has something to animate.
      var current = panel.scrollHeight;
      panel.style.height = current + "px";
      // Force reflow before collapsing.
      // eslint-disable-next-line no-unused-expressions
      panel.offsetHeight;
      requestAnimationFrame(function () {
        panel.style.height = "0px";
      });
    }
  };

  CQAccordion.prototype.openItem = function (item) {
    if (item && !item.classList.contains("is-open")) {
      this.toggle(item);
    }
  };

  /* ---------- boot / registry ---------- */
  var instances = new WeakMap();

  function init(scope) {
    var sections = (scope || document).querySelectorAll(".cq-section[data-cq]");
    Array.prototype.forEach.call(sections, function (section) {
      var list = section.querySelector(".cq-list");
      if (!list || instances.has(section)) return;
      instances.set(section, new CQAccordion(list));
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init();
    });
  } else {
    init();
  }

  /* ---------- Shopify theme editor hooks ---------- */
  document.addEventListener("shopify:section:load", function (e) {
    init(e.target);
  });

  document.addEventListener("shopify:section:unload", function (e) {
    var section = e.target.querySelector(".cq-section[data-cq]");
    if (section) instances.delete(section);
  });

  // Open the block a merchant is editing.
  document.addEventListener("shopify:block:select", function (e) {
    var item = e.target.closest ? e.target.closest(".cq-item") : null;
    if (!item) return;
    var list = item.closest(".cq-list");
    var inst = list && instances.get(list.closest(".cq-section"));
    if (inst) inst.openItem(item);
  });
})();
